import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from 'react-router-dom'
import useAuthenticationContext from '../../../contexts/Authentication/Authentication.logic/useAuthenticationContext'
import Top10AView from "../Dashboarding.containers/Top10AView"
import GeneralView from "../Dashboarding.containers/GeneralView"
import DeliveryEntitiesView from "../Dashboarding.containers/DeliveryEntitiesView"
import DashboardLoader from "./DashboardLoader"
import DebugConsole from "./DebugConsole"
import DatabaseContext from "../../../contexts/Database/DatabaseContext"
import logoWondergrip from '../Dashboarding.assets/logo-wondergrip-new.png'
import './DashboardingPageView.css'

// 🎯 Generic hook for automatic page state persistence
const usePageState = (initialState = {}, pageKey = null) => {
  // Generate unique key for this page
  const storageKey = `pageState_${pageKey || window.location.pathname}`

  // Get initial state (either from localStorage or default)
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsedState = JSON.parse(saved)
        console.log(`🔄 Restored state for ${storageKey}:`, parsedState)
        return { ...initialState, ...parsedState }
      }
    } catch (error) {
      console.warn(`Failed to restore state for ${storageKey}:`, error)
    }
    return initialState
  }

  const [state, setState] = useState(getInitialState())

  // Auto-save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(state))
    console.log(`💾 Saved state for ${storageKey}:`, state)
  }, [state, storageKey])

  // Helper to update specific state properties
  const updateState = (updates) => {
    setState(prevState => ({ ...prevState, ...updates }))
  }

  return { state, updateState }
}

// Hamburger button component
const Hamburger = ({ sidebarOpen, setSidebarOpen }) => (
  <button
    type="button"
    className="dashboard-hamburger"
    aria-label="Open menu"
    onClick={() => setSidebarOpen(true)}
    style={{
      position: 'absolute',
      top: 16,
      left: 16,
      zIndex: 1300,
      background: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      padding: 4,
      height: '28px',
      width: '28px',
      display: sidebarOpen ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '3px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }}
  >
    <span className="dashboard-hamburger-bar" />
    <span className="dashboard-hamburger-bar" />
    <span className="dashboard-hamburger-bar" />
  </button>
)

// Overlay component for mobile
const Overlay = ({ sidebarOpen, setSidebarOpen }) => (
  <div
    className="dashboard-sidebar-overlay"
    role="button"
    tabIndex={0}
    aria-label="Close sidebar"
    onClick={() => setSidebarOpen(false)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        setSidebarOpen(false)
      }
    }}
    style={{
      display: sidebarOpen && window.innerWidth < 1240 ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.2)',
      zIndex: 1100,
    }}
  />
)

const DashboardingPageView = () => {
  // 🎯 Simple page state with automatic persistence
  const { state, updateState } = usePageState({
    activeView: 'top10a',
    sidebarOpen: window.innerWidth >= 1240,
  }, 'dashboard')

  const { activeView, sidebarOpen } = state
  const { isLoading: dbLoading, refetch } = useContext(DatabaseContext)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const navigate = useNavigate()
  const { userLogout } = useAuthenticationContext()

  // Update activeView
  const setActiveView = (newView) => {
    updateState({ activeView: newView })
  }

  // Update sidebarOpen
  const setSidebarOpen = (newSidebarOpen) => {
    updateState({ sidebarOpen: newSidebarOpen })
  }

  // Handle refresh data
  const handleRefreshData = async () => {
    setIsRefreshing(true)
    try {
      await refetch()
      console.log('✅ Data refreshed successfully')
    } catch (error) {
      console.error('❌ Error refreshing data:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleLogout = async () => {
    try {
      await userLogout()
      navigate('/')
    } catch (error) {
      console.error('❌ Error during logout:', error)
      // Force navigation even if logout fails
      navigate('/')
    }
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1240) {
        setSidebarOpen(false)
      } else {
        setSidebarOpen(true)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Show loader while database is loading (after all hooks)
  if (dbLoading) {
    return <DashboardLoader />
  }

  const renderActiveView = () => {
    switch (activeView) {
      case 'top10a':
        return <Top10AView />
      case 'general':
        return <GeneralView />
      case 'delivery':
        return <DeliveryEntitiesView />
      default:
        return <Top10AView />
    }
  }

  const sidebarStyle = {
    width: sidebarOpen ? '280px' : '0',
    minWidth: sidebarOpen ? '200px' : '0',
    background: '#000000',
    padding: sidebarOpen ? '32px 0' : '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
    zIndex: 1200,
    position: 'relative',
  }

  const logoContainerStyle = {
    padding: '0 24px',
    marginBottom: '8px',
    display: sidebarOpen ? 'block' : 'none',
    transition: 'all 0.2s',
  }

  const logoStyle = {
    color: '#FFFFFF',
    fontSize: '28px',
    fontWeight: '800',
    fontFamily: 'Montserrat, sans-serif',
    letterSpacing: '1px',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }

  const subtitleStyle = {
    color: '#FFFFFF',
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    opacity: 0.7,
    marginTop: '4px',
  }

  const buttonBaseStyle = {
    display: 'block',
    width: '100%',
    padding: '16px 24px',
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    fontFamily: 'Montserrat, sans-serif',
    transition: 'all 0.2s ease-in-out',
    letterSpacing: '0.5px',
  }

  const getButtonStyle = (viewName) => ({
    ...buttonBaseStyle,
    ...(activeView === viewName && {
      color: '#FFFFFF',
      background: 'rgba(71, 144, 201, 0.1)',
      borderLeft: '4px solid #4790C9',
    }),
    '&:hover': {
      background: 'rgba(255,255,255,0.05)',
    },
  })

  return (
    <div style={{ display: 'flex', height: '100vh', position: 'relative' }}>
      <Hamburger sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Overlay sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div style={logoContainerStyle}>
          <img 
            src={logoWondergrip} 
            alt="WonderGrip - Redefining Hand Protection" 
            style={{
              maxWidth: '100%',
              height: 'auto',
              maxHeight: '120px'
            }}
          />
        </div>
        <nav style={{ display: sidebarOpen ? 'block' : 'none' }}>
          <button
            type="button"
            onClick={() => {
              setActiveView('top10a')
              if (window.innerWidth < 1240) setSidebarOpen(false)
            }}
            style={getButtonStyle('top10a')}
          >
            Top X
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveView('general')
              if (window.innerWidth < 1240) setSidebarOpen(false)
            }}
            style={getButtonStyle('general')}
          >
            General View
          </button>
          {/* <button
            type="button"
            onClick={() => {
              setActiveView('delivery')
              if (window.innerWidth < 1240) setSidebarOpen(false)
            }}
            style={getButtonStyle('delivery')}
          >
            Delivery Entities
          </button> */}

          {/* Refresh Button */}
          <button
            type="button"
            onClick={handleRefreshData}
            disabled={isRefreshing}
            style={{
              ...buttonBaseStyle,
              marginTop: '20px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '20px',
              color: isRefreshing ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.7)',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
            }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>

          {/* Logout Button */}
          <button
            type="button"
            onClick={handleLogout}
            style={{
              ...buttonBaseStyle,
              marginTop: '20px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '20px',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </nav>
      </div>
      {/* Main content */}
      <div style={{ flex: 1, background: '#FFFFFF', display: 'flex', flexDirection: 'column' }}>
        {/* Header with Debug Console */}
        <div style={{
          padding: '16px 24px 0 24px',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
        >
          <DebugConsole />
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {renderActiveView()}
        </div>
      </div>
    </div>
  )
}

export default DashboardingPageView
